/**
 * Auth Slice
 * 
 * Manages authentication state and actions
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/authService';
import { fetchCVODashboard, fetchAuthorityDashboard, clearDashboard } from './dashboardSlice';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  role: null, // 'cvo' | 'authority'
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await authService.login(credentials);
      if (response.success) {
        // Fetch dashboard data based on user role after successful login
        const userRole = response.user?.role?.name || response.user?.role || response.user?.user_role;

        if (userRole === 'cvo') {
          dispatch(fetchCVODashboard());
        } else if (userRole === 'authority') {
          dispatch(fetchAuthorityDashboard());
        }

        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await authService.logout();
      if (response.success) {
        // Clear dashboard data on logout
        dispatch(clearDashboard());
        return true;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.sendOtp(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.error || response.message || 'Failed to send OTP');
    } catch (error) {
      return rejectWithValue(error.error || error.message || 'Failed to send OTP');
    }
  }
);

export const validateSendOtp = createAsyncThunk(
  'authority/sendOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.validateSendOtp(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const authoritySendOtp = createAsyncThunk(
  'authority/sendOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.authoritySendOtp(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.error || response.message || 'Failed to send OTP');
    } catch (error) {
      return rejectWithValue(error.error || error.message || 'Failed to send OTP');
    }
  }
);

export const userSendValidationOtp = createAsyncThunk(
  'user/sendValidationOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.userSendValidationOtp(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.error || response.message || 'Failed to send OTP');
    } catch (error) {
      return rejectWithValue(error.error || error.message || 'Failed to send OTP');
    }
  }
);

export const userVerifyValidationOtp = createAsyncThunk(
  'user/verifyValidationOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.userVerifyValidationOtp(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const authorityVerifyOtp = createAsyncThunk(
  'authority/verifyOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.authorityVerifyOtp(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.error || response.message || 'Failed to verify OTP');
    } catch (error) {
      return rejectWithValue(error.error || error.message || 'Failed to verify OTP');
    }
  }
);

export const cvoSendOtp = createAsyncThunk(
  'cvo/sendOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.cvoSendOtp(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.error || response.message || 'Failed to send OTP');
    } catch (error) {
      return rejectWithValue(error.error || error.message || 'Failed to send OTP');
    }
  }
);

export const cvoVerifyOtp = createAsyncThunk(
  'cvo/verifyOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authService.cvoVerifyOtp(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.error || response.message || 'Failed to verify OTP');
    } catch (error) {
      return rejectWithValue(error.error || error.message || 'Failed to verify OTP');
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await authService.verifyOtp(data);
      if (response.success) {
        // Fetch dashboard data based on user role after successful OTP verification
        const userRole = response.user?.role?.name || response.user?.role || response.user?.user_role;

        if (userRole === 'cvo') {
          dispatch(fetchCVODashboard());
        } else if (userRole === 'authority') {
          dispatch(fetchAuthorityDashboard());
        }

        return response.data;
      }
      return rejectWithValue(response.error || response.message || 'Failed to verify OTP');
    } catch (error) {
      return rejectWithValue(error.error || error.message || 'Failed to verify OTP');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Synchronous actions
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setRole: (state, action) => {
      state.role = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.role = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.role = action.payload.user?.role;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.role = null;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        // Clear state even on error
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.role = null;
      });

    // Send OTP
    builder
      .addCase(sendOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Verify OTP
    builder
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.role = action.payload.user?.role;
        state.error = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Authority Send OTP
    builder
      .addCase(authoritySendOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authoritySendOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(authoritySendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Authority Verify OTP
    builder
      .addCase(authorityVerifyOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authorityVerifyOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(authorityVerifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // User Send Validation OTP
    builder
      .addCase(userSendValidationOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(userSendValidationOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(userSendValidationOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // User Verify Validation OTP
    builder
      .addCase(userVerifyValidationOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(userVerifyValidationOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(userVerifyValidationOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // CVO Send OTP
    builder
      .addCase(cvoSendOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cvoSendOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(cvoSendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // CVO Verify OTP
    builder
      .addCase(cvoVerifyOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cvoVerifyOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(cvoVerifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const { setUser, setToken, setRole, clearAuth, clearError } = authSlice.actions;

// Export selectors
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectUserRole = (state) => state.auth.role;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;

// Export reducer
export default authSlice.reducer;

